// Teaching Shooting & Skill Acquisition Concepts. Cert masterclass.
// Mux: 02xXxVqv4IW00CUqsD19YCvnCGQO01Mu29h8hGWcpmCgt8
//
// Authored from the talk's auto-caption transcript
// (knowledge-base/transcripts/cert/Teaching-Shooting-and-Skill-Acquisition-Concepts.md).
// A conversation between Coleman and NBA shooting coach Dave Love.
// Quotes are lifted from what they actually said. Bullets distill the
// practical takeaway. Plain language, coach to coach, no em dashes.

export const SKILL_ACQUISITION_SHOOTING_MODULE_KEY = "teaching-shooting-skill-acquisition";

const VIDEO = "02xXxVqv4IW00CUqsD19YCvnCGQO01Mu29h8hGWcpmCgt8";

export const SKILL_ACQUISITION_SHOOTING_MODULE = {
  key: SKILL_ACQUISITION_SHOOTING_MODULE_KEY,
  title: "Teaching Shooting & Skill Acquisition Concepts",
  sub: "A working conversation with NBA shooting coach Dave Love on how players actually learn, why the gym shooter stalls in the game, and how to teach a shot that holds up under fatigue, pressure, and a hand in the face.",
  coverVideoId: VIDEO,
  duration: "Full talk plus a guided breakdown",
  chapters: [
    {
      id: "watch",
      title: "Watch the talk",
      blocks: [
        { kind: "text", md:
          "Watch it once start to finish, then come back through these sections for the ideas worth keeping. This is Coleman in conversation with NBA shooting coach Dave Love, so the chapters follow the real flow of their talk: from where to start with skill acquisition, to implicit learning, to what a shot has to do to survive a game." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Teaching Shooting & Skill Acquisition Concepts." },
      ],
    },

    {
      id: "why-game-makes-the-player",
      title: "1. The game develops players more than your drills",
      blocks: [
        { kind: "text", md:
          "Both Coleman and Dave hit on the same thing they keep seeing: coaches who quit organized ball and just start playing take a massive leap as players. Same story shows up everywhere. So before any drill talk, sit with why that happens, because it tells you what real development looks like." },
        { kind: "text", md:
          "Coleman gives three reasons. The mental freedom (you stop identifying as a player, so you shoot more freely and experiment). The shift from drills as the main thing to **play as the main thing**. And the brain simply maturing and seeing patterns it could not see before." },
        { kind: "pullquote", md:
          "It just flips to more so how skill development should be, where you're playing against live defense, you're experimenting with things." },
        { kind: "callout", tone: "warn", title: "The confounding variable trap", body:
          "A trainer does some two-ball stuff and spot shooting, the player gets better, and the trainer takes the credit. But that player is also in open runs against NBA guys three days a week. What got them better, the two-ball drills or hooping against the best players in the world? Be honest about what is actually doing the work in your players." },
      ],
    },

    {
      id: "where-to-start",
      title: "2. Where to start with skill acquisition",
      blocks: [
        { kind: "text", md:
          "If these concepts are foreign to you, the volume of research is overwhelming. Dave and Coleman lay out a starter set so you do not drown in it. Begin here and add on piece by piece." },
        { kind: "principles", items: [
          { n: 1, title: "Constraints", body:
            "A task or rule you set up so the player learns something without you explaining it. The drill does the teaching." },
          { n: 2, title: "The challenge point", body:
            "The right level of difficulty for this player right now. Too easy teaches nothing, too hard makes them quit." },
          { n: 3, title: "Block, variable, random", body:
            "The three ways to structure reps. Knowing when to use each is most of practice design." },
          { n: 4, title: "Implicit vs explicit learning", body:
            "Whether the player learns by being told the answer or by discovering it through the task. This one changes how you coach everything." },
        ]},
        { kind: "callout", tone: "tip", title: "Read past one book", body:
          "Coleman's warning: when people subscribe to only one book or one philosophy, that is where it gets dangerous. Learn a concept, then go find your own research on it and try new things. That is how you build a real understanding instead of just repeating someone else's." },
      ],
    },

    {
      id: "make-it-challenging",
      title: "3. Make the training actually challenging",
      blocks: [
        { kind: "text", md:
          "Before any fancy method, Coleman calls this the true foundation, and the box most workouts fail to check. Players improve when they sit in what Daniel Coyle called the optimal zone of improvement: a little frustrated, but not so much that they give up." },
        { kind: "pullquote", prominent: true, md:
          "How do we make training more challenging without just creating the facade of, oh, it's fatiguing, therefore it's challenging? How do we actually challenge the skill behind it?" },
        { kind: "text", md:
          "Getting a player tired is not the same as challenging the skill. Read that twice. A drill can leave a player gassed and teach nothing. The question is always whether the **skill itself** was put under real demand, not whether the player is sweating." },
        { kind: "assess", id: "challenge-check", prompt:
          "In your typical session, how much of the difficulty actually challenges the skill versus just creating fatigue?",
          lowLabel: "Mostly just fatigue", highLabel: "Mostly real skill demand" },
      ],
    },

    {
      id: "implicit-learning",
      title: "4. Implicit learning: teach without telling",
      blocks: [
        { kind: "text", md:
          "This was the idea Matt, the host, had never heard before, and it is one of Coleman's biggest. Most of us teach the way school taught us: name the thing, explain it, have them memorize it. That is explicit learning. But players learn things constantly without knowing how they learned them." },
        { kind: "pullquote", md:
          "Players will learn things all the time that they don't know how they learned. You ask the best players you work with, how'd you learn that move? And they'll say, I don't know." },
        { kind: "text", md:
          "Instead of explaining, you design the drill so the player discovers the solution. Coleman's example: a player with a hitch and no energy transfer. You do not lecture them about leg timing. You say shoot from the end of your range and you are not allowed to jump. They self-organize to find the power. They feel it instead of hearing it." },
        { kind: "text", md:
          "Dave's version for hand placement: instead of telling a player to put the shooting hand under the middle of the ball, take the guide hand off. They self-organize and find the middle of the ball on their own. The feedback teaches them what to adjust." },
        { kind: "callout", tone: "tip", title: "Slower to learn, longer to keep", body:
          "Implicit learning takes longer than explicit learning. But athletes retain it better. Two weeks down the line they are still better at the skill, where explicitly coached cues often fizzle out after the session ends. You are trading speed today for retention later." },
      ],
    },

    {
      id: "the-chair-drill",
      title: "5. Constraints in action: the chair and the lean",
      blocks: [
        { kind: "text", md:
          "Both coaches share constraint drills for balance that no one had to explain to the player. The player figures it out because the task forces it." },
        { kind: "compare",
          leftTitle: "Coleman's chair",
          leftItems: [
            "Stack two chairs in front of the player",
            "Tell them: stop yourself, do not run into the chair",
            "He literally chucks them toward it",
            "They figure out how to stop themselves to avoid the hit",
          ],
          rightTitle: "Dave's lean",
          rightItems: [
            "Player leans full body weight into the coach",
            "Coach pulls away, player must find balance into the shot",
            "Coleman's wrinkle: stand behind, have them lean back into you",
            "Never says a word about foot placement or center of mass",
          ],
        },
        { kind: "pullquote", md:
          "I never mentioned anything about get your left foot outside your center of mass. They're going to figure it out because they don't want to hit that chair." },
        { kind: "text", md:
          "Notice what both drills share. The coach sets up a task with a clear consequence, then gets out of the way. The learning is more robust, and as Coleman points out, it is more fun for the player too." },
      ],
    },

    {
      id: "rethinking-form-shooting",
      title: "6. Rethinking form shooting",
      blocks: [
        { kind: "text", md:
          "Traditional form shooting is shooting slowly from right under the basket. Dave pushed Coleman to question whether that is even shooting at all, and it stuck." },
        { kind: "pullquote", md:
          "Form shooting under the basket is not really form shooting, because you're not shooting an actual shooting form. You're just placing the ball in the rim." },
        { kind: "text", md:
          "Coleman now splits it into two different things. When you shoot close to see the ball go in a bunch, call it what it is: **psychological momentum shooting**. It builds confidence, not form. Real form shooting happens from a realistic distance, because only then do you get the actual forces and feel of a real shot." },
        { kind: "callout", tone: "warn", title: "Name your drills honestly", body:
          "If a drill builds confidence, do not pretend it is building mechanics. Mislabeling what a drill does is how a workout fills up with reps that feel productive but never transfer. Decide what each drill is for, and put it at the distance and conditions that serve that purpose." },
      ],
    },

    {
      id: "but-it-works",
      title: "7. \"But it works\" is the trap",
      blocks: [
        { kind: "text", md:
          "Coleman's biggest pet peeve is the phrase he hears constantly: but it works. Dave frames it with blackjack. You can win hands without knowing to split aces. But if you learn it, the odds tilt in your favor over time. Skill acquisition is those principles for training." },
        { kind: "pullquote", prominent: true, md:
          "As an industry we may get players from 50 to 70 percent of their potential. That works. So why can't we shoot to get them to 90? Why not see if it works more?" },
        { kind: "text", md:
          "Coleman's other warning is the 20 year doctor. Studies show doctors with 20 years experience can be worse than five year doctors, because at some point you stop learning and the field moves on without you. The five year doctor is grinding through modern research and pulling ahead." },
        { kind: "callout", tone: "quote", title: "The most expensive words", body:
          "Coleman quotes the business line: the most dangerous and expensive words are this is how we've always done it. It works, so why look at anything else. That sentence is where coaches stop growing." },
      ],
    },

    {
      id: "different-players-different-training",
      title: "8. A 13 year old should not train like Steph",
      blocks: [
        { kind: "text", md:
          "Dave asks Coleman to punch holes in the idea that a 13 year old who wants to shoot should train the way Steph Curry already trains. Coleman's answer reframes how you read what great players do." },
        { kind: "pullquote", prominent: true, md:
          "Just because a player is at a certain spot in status, skill, or success doesn't mean the work they're currently doing got them there. It's just something they're doing." },
        { kind: "text", md:
          "The work Steph does at 28 is not what made him great. A lot of that traces to early childhood, natural ability, and how he played growing up (Coleman points to Kyrie Irving's backboard with a hole in it as an environmental constraint that shaped his creativity). And the best players often cannot even tell you what they do. There are videos of Steph describing space between ball and palm, then his actual form has none. He is so good he does not think about it." },
        { kind: "text", md:
          "A 13 year old and Steph also have vastly different individual constraints: limb length, dominant eye, strength to stop themselves, how consolidated their form is. Different players, different starting points, different training." },
        { kind: "callout", tone: "tip", title: "Functional movement variability", body:
          "Experts shoot a wide open, controlled shot almost identically every time. But in a game (contested, tired, off balance) they vary the technique and still get the same result: drop a little lower when tired, fade and kick the right leg, whatever it takes. That is functional movement variability. A 13 year old does not even have a consistent unchanging shot yet, which is exactly why there is more value in some block practice for them first." },
      ],
    },

    {
      id: "non-negotiables",
      title: "9. Shooting non-negotiables, result first",
      blocks: [
        { kind: "text", md:
          "Dave's shooting non-negotiables are worded to leave wiggle room in how a player gets there: be as balanced as possible, push both sides of the ball evenly, get the ball up and dropping down on the hoop. Coleman's are even more result based, almost too obvious." },
        { kind: "principles", items: [
          { n: 1, title: "The ball goes straight", body:
            "Left to right, it has to be on line. Non-negotiable result." },
          { n: 2, title: "It gets high enough to come down into the rim", body:
            "Trajectory. The ball has to arc to go in, not line drive at the front iron." },
          { n: 3, title: "It has the power to get there", body:
            "Coleman calls the cause flow or energy transfer. Enough power on the shot so it reaches the rim, usually a byproduct of good flow." },
        ]},
        { kind: "pullquote", md:
          "You could shoot a backwards granny shot, and if those three things happen and you can get it off at every level with fatigue and anxiety, I'm not going to change it." },
        { kind: "text", md:
          "The technical stuff (getting on balance, hand placement) is what Coleman likes to see, but it is secondary. He looks at the result first: does it go straight, does it go high enough, does it get there. Everything else is a tweak in service of those three." },
      ],
    },

    {
      id: "scalable-shot",
      title: "10. Will the shot survive the next level?",
      blocks: [
        { kind: "text", md:
          "Coleman's second non-negotiable is whether the shot is scalable to higher levels, fatigue, and anxiety. A shot that works in high school may not survive faster closeouts and taller athletes in college. The fix is a tweak, not a rebuild." },
        { kind: "compare",
          leftTitle: "Three stressors a shot must hold up to",
          leftItems: [
            "Level: faster closeouts, taller, more athletic defenders",
            "Fatigue: tired players lose power and range first",
            "Range: a step past the line exposes a weak base",
          ],
          rightTitle: "Why slow shots crack under pressure",
          rightItems: [
            "A really slow shot gives the player time to think",
            "Clutch moment, no time to settle, overthinking creeps in",
            "Quick trigger leaves no window to reinvest thought and choke",
            "Steph just catch and shoot, boom, no time to doubt it",
          ],
        },
        { kind: "text", md:
          "Test the shot against the conditions it will actually face. If a player only ever shoots fresh, alone, and at one distance, you have no idea whether the shot scales. Build the stressors in on purpose so you find out in the gym, not in the game." },
      ],
    },

    {
      id: "mental-side",
      title: "11. Training the mental side",
      blocks: [
        { kind: "text", md:
          "Coleman grew up overthinking everything, with anxiety on normal shots, so he knows how much potential gets left on the table. He builds the mental side in on purpose instead of treating it as cliche." },
        { kind: "pullquote", prominent: true, md:
          "These competitions where we're just micro-dosing pressure and anxiety into sessions, to me that's huge. It exposes you to how it feels to shoot with a little pressure." },
        { kind: "text", md:
          "Simple pressure drills do the work: make as many as you can without missing two in a row, go against your record or a teammate. Up at 15, miss one, now you cannot miss the next one. That is real repetition with pressure inducing shots. It also trains entering the flow state, and players who can reach the zone shoot better game to game." },
        { kind: "text", md:
          "Dave adds the mindset frame. Lean on knowledge of process over knowledge of results, because early on the results are not there yet. And understand that **confidence is a byproduct, not a product**. If you hand a player confidence before the results exist, it evaporates the moment the results do not match. Do the work, see the results, and the confidence is earned." },
        { kind: "multipick", id: "mental-tools", prompt:
          "Which of these will you actually add to your next few sessions to train the mental side?",
          options: [
            "A make-streak or no-two-misses pressure game",
            "Head to head competition against a record or a teammate",
            "Cues that help the player let a miss go",
            "Framing early progress around process, not makes",
            "Letting confidence come from earned results, not pep talks",
          ]},
      ],
    },

    {
      id: "stay-a-student",
      title: "12. Stay a student, then trust your craft",
      blocks: [
        { kind: "text", md:
          "By the end, Coleman is at the spot where the skill acquisition books start repeating each other. His move is not to stop, it is to look outside the field to learn more about it: psychology, English Premier League soccer, how other sports practice and move. Understanding basketball's impact on people loops back into skill acquisition too." },
        { kind: "pullquote", md:
          "I'm looking externally from skill acquisition to learn more about skill acquisition, which is a fun spot to be in." },
        { kind: "text", md:
          "Dave's closing advice balances it. Do not blindly follow the shepherd. A lot of social media shooting coaches all say the same things, and some of it is wrong, and everyone piggybacks anyway. Sometimes you have to be a lone wolf, find where the gaps are, and build your own solution, even if you are sometimes wrong." },
        { kind: "callout", tone: "quote", title: "The North Star", body:
          "Dave: you learn more in the gym working with individual players than you do from the research. Learn the research, then learn to apply it. That is where the skill is. Understand the science and the tradition, but blindly follow neither." },
        { kind: "checklist", id: "skill-acq-takeaways", title: "What to actually do Monday", items: [
          "Make at least one drill challenge the skill, not just create fatigue",
          "Pick one cue you usually say out loud and turn it into a constraint instead",
          "Rename your close-range reps honestly (confidence vs real form shooting)",
          "Stress-test a player's shot against fatigue, range, or pressure this week",
          "Micro-dose pressure with a make-streak competition",
          "Read or watch one thing outside basketball and steal an idea from it",
        ]},
        { kind: "recap", id: "recap", title: "Your reflections from this masterclass" },
      ],
    },
  ],
};
